import {
  ArrowUpRight,
  BadgeCheck,
  LineChart,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ClubLogo } from './ClubLogo';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { trainingForWeakness } from './trainingLibrary';

type DevelopmentSignal = {
  playerId: string;
  weakness: string;
  domain: 'technical' | 'tactical' | 'physical' | 'mental';
  severity: number;
  note: string;
  matchId: string;
  updatedAt: string;
};

type ProgressEntry = {
  id: string;
  playerId: string;
  weakness: string;
  score: number;
  note: string;
  date: string;
};

const PROGRESS_KEY = 'ecc_development_progress_v1';

function readSignals(teamId: string, playerId: string): DevelopmentSignal[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(`ecc_development_signal_${teamId}_${playerId}`) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function loadProgress(): ProgressEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function DevelopmentProgress() {
  const { t } = useI18n();
  const { team, players } = useData();
  const [selectedPlayerId, setSelectedPlayerId] = useState(players[0]?.id || '');
  const [progress, setProgress] = useState<ProgressEntry[]>(loadProgress);
  const [score, setScore] = useState(50);
  const [note, setNote] = useState('');

  const selectedPlayer = players.find((player) => player.id === selectedPlayerId) || players[0] || null;
  const signals = team && selectedPlayer ? readSignals(team.id, selectedPlayer.id) : [];
  const weakness = signals[0]?.weakness || 'first_touch';
  const method = trainingForWeakness(weakness);

  const entries = useMemo(
    () =>
      selectedPlayer
        ? progress
            .filter((entry) => entry.playerId === selectedPlayer.id && entry.weakness === weakness)
            .sort((a, b) => a.date.localeCompare(b.date))
        : [],
    [progress, selectedPlayer, weakness],
  );

  const latest = entries.at(-1)?.score ?? null;
  const first = entries[0]?.score ?? null;
  const delta = latest !== null && first !== null ? latest - first : 0;

  function saveCheckpoint() {
    if (!selectedPlayer) return;
    const next = [
      ...progress,
      {
        id: crypto.randomUUID(),
        playerId: selectedPlayer.id,
        weakness,
        score: Math.max(0, Math.min(100, Number(score))),
        note: note.trim(),
        date: new Date().toISOString(),
      },
    ];
    setProgress(next);
    try {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(next));
    } catch {
      // Local-only progress history.
    }
    setNote('');
  }

  return (
    <section className="metal-card development-progress">
      <div className="section-title-line">
        <div>
          <p className="eyebrow">DEVELOPMENT PROGRESS</p>
          <h2>{t.developmentProgress}</h2>
          <p className="muted">{t.developmentProgressSubtitle}</p>
        </div>
        <LineChart size={24} />
      </div>

      <div className="development-progress-layout">
        <aside className="development-progress-playerlist">
          {players.map((player) => (
            <button key={player.id} className={selectedPlayer?.id === player.id ? 'selected' : ''} onClick={() => setSelectedPlayerId(player.id)}>
              <span>
                {player.photo_url
                  ? <img src={player.photo_url} alt="" />
                  : <ClubLogo club="Polonia Glasgow" className="progress-player-logo" />}
              </span>
              <div>
                <strong>#{player.squad_number ?? '—'} {player.name}</strong>
                <small>{player.position || '—'}</small>
              </div>
            </button>
          ))}
        </aside>

        <div className="development-progress-main">
          {selectedPlayer ? (
            <>
              <div className="development-progress-head">
                <div>
                  <span>{t.currentFocus}</span>
                  <strong>{method.label}</strong>
                </div>
                <div>
                  <span>{t.progress}</span>
                  <strong className={delta >= 0 ? 'positive' : 'negative'}>
                    {delta >= 0 ? '+' : ''}{delta}
                  </strong>
                </div>
              </div>

              <div className="development-history">
                {entries.map((entry, index) => (
                  <div key={entry.id}>
                    <span className="development-history-dot" />
                    <div>
                      <strong>{entry.score}/100</strong>
                      <small>{new Date(entry.date).toLocaleDateString()}</small>
                      {entry.note && <p>{entry.note}</p>}
                    </div>
                    {index > 0 && entry.score > entries[index - 1].score && <ArrowUpRight size={16} />}
                  </div>
                ))}
                {!entries.length && (
                  <div className="development-progress-empty">
                    <BadgeCheck size={20} />
                    <span>{t.noProgressCheckpoints}</span>
                  </div>
                )}
              </div>

              <div className="development-checkpoint">
                <label>
                  <span>{t.developmentScore}</span>
                  <input type="range" min="0" max="100" value={score} onChange={(event) => setScore(Number(event.target.value))} />
                  <strong>{score}/100</strong>
                </label>
                <textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder={t.progressNotePlaceholder} />
                <button className="gold-button" onClick={saveCheckpoint}>
                  <Target size={16} />
                  {t.saveCheckpoint}
                </button>
              </div>
            </>
          ) : (
            <p className="muted">{t.noData}</p>
          )}
        </div>
      </div>
    </section>
  );
}
